/**
 * Main-column panel for the np_selected triage step — replaces the
 * legacy per-item picker. Composes the collapsible full-scope grid, the
 * assignments list, and the advance bar. Reads everything from
 * NpAssignmentsContext (so the page-registered aside shares the state).
 */

import { useState } from 'react';

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  List,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconFileAlert, IconUserExclamation } from '@tabler/icons-react';

import { ApiConflictError } from '@/services/vnext-api';
import { isManagedAssignment, type BackofficeAssignment } from '@/types/np-assignments';
import { AssignmentCard } from './AssignmentCard';
import { useNpAssignmentsContext } from './NpAssignmentsContext';
import { ScopeGrid } from './ScopeGrid';

/** Backoffice-rejection details from the 409 raised when creating the
 *  project at READY_TO_START (distinct from the readiness-gate
 *  missing_item_ids). All arrays optional; at least one is non-empty. */
export interface BackofficeMissing {
  missingPartners: string[];
  missingDocuments: string[];
  missingSubjects: string[];
  missingComponents: string[];
}

function readBackofficeMissing(detail: Record<string, unknown>): BackofficeMissing | null {
  const partners = (detail.missing_partners as string[]) ?? [];
  const documents = (detail.missing_documents as string[]) ?? [];
  const subjects = (detail.missing_subjects as string[]) ?? [];
  const components = (detail.missing_components as string[]) ?? [];
  if (
    partners.length === 0 &&
    documents.length === 0 &&
    subjects.length === 0 &&
    components.length === 0
  ) {
    return null;
  }
  return {
    missingPartners: partners,
    missingDocuments: documents,
    missingSubjects: subjects,
    missingComponents: components,
  };
}

interface NpAssignmentsPanelProps {
  /** Advance callback — fires NP_SELECTED → READY_TO_START for triage, or dispatch for escalations. */
  readonly onAdvance: () => Promise<unknown>;
  /** Label for the submit button. Defaults to "Continue to Ready". */
  readonly submitLabel?: string;
}

export function NpAssignmentsPanel({
  onAdvance,
  submitLabel = 'Continue to Ready',
}: NpAssignmentsPanelProps) {
  const { assignments, humanItems, unassignedHumanItemIds, ready, isLoading } =
    useNpAssignmentsContext();
  const [advancing, setAdvancing] = useState(false);
  const [missingIds, setMissingIds] = useState<string[]>([]);
  const [backofficeMissing, setBackofficeMissing] = useState<BackofficeMissing | null>(null);

  if (isLoading && assignments.length === 0 && humanItems.length === 0) {
    return (
      <Card shadow="sm" padding="lg" radius="md">
        <Group>
          <Loader size="sm" />
          <Text size="sm">Loading assignments…</Text>
        </Group>
      </Card>
    );
  }

  // No HUMAN items at all → nothing to assign.
  if (humanItems.length === 0 && assignments.length === 0) {
    return (
      <Card shadow="sm" padding="lg" radius="md">
        <Stack gap="sm">
          <Title order={4}>4. NP assignments</Title>
          <Text size="sm" c="dimmed">
            This report has no items that need a Network Partner — continue.
          </Text>
          <Group>
            <Button onClick={() => void handleAdvance()} loading={advancing}>
              Continue to Ready
            </Button>
          </Group>
        </Stack>
      </Card>
    );
  }

  async function handleAdvance() {
    setAdvancing(true);
    setMissingIds([]);
    setBackofficeMissing(null);
    try {
      await onAdvance();
    } catch (err) {
      if (err instanceof ApiConflictError) {
        // Two distinct 409s arrive here: the readiness gate
        // (missing_item_ids) and the Backoffice create rejection
        // (missing_partners / _documents / _subjects / _components).
        const ids = (err.detail.missing_item_ids as string[]) ?? [];
        setMissingIds(ids);
        setBackofficeMissing(readBackofficeMissing(err.detail));
      }
    } finally {
      setAdvancing(false);
    }
  }

  const allGrouped = unassignedHumanItemIds.length === 0;
  const canAdvance = allGrouped && ready;
  // The automatic registry assignment is kept by its orders and never
  // locked by hand, so it is not one of the assignments to get ready.
  const counted = assignments.filter((a) => !isManagedAssignment(a));

  return (
    <Stack gap="md">
      <Card shadow="sm" padding="lg" radius="md">
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Title order={4}>NP assignments</Title>
            <Text size="sm" c="dimmed">
              Group items into assignments, agree a fee and turnaround with a Network Partner over
              chat, then lock one in per assignment.
            </Text>
          </Stack>
          <Badge color={canAdvance ? 'green' : 'yellow'} variant="light">
            {counted.filter((a) => a.isReady).length} / {counted.length} ready
          </Badge>
        </Group>
      </Card>

      <ScopeGrid />

      <ReadinessConflictAlerts missingIds={missingIds} backofficeMissing={backofficeMissing} />

      {assignments.length > 0 ? (
        <Stack gap="md">
          {assignments.map((a, idx) => (
            <AssignmentCard key={a.id} assignment={a} index={idx} />
          ))}
        </Stack>
      ) : (
        <Card shadow="sm" padding="lg" radius="md">
          <Text size="sm" c="dimmed">
            No assignments yet — select items in the scope above and click “Create assignment”.
          </Text>
        </Card>
      )}

      <Card shadow="sm" padding="lg" radius="md">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {canAdvance
              ? 'Every item is covered by a ready assignment.'
              : !allGrouped
                ? `${unassignedHumanItemIds.length} item(s) not yet in an assignment.`
                : 'Every assignment needs a locked NP, fee, and due date.'}
          </Text>
          <Button
            onClick={() => void handleAdvance()}
            loading={advancing}
            disabled={!canAdvance}
            data-testid="continue-to-ready-btn"
          >
            {submitLabel}
          </Button>
        </Group>
      </Card>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Shared 409 alerts — the readiness gate + the Backoffice create rejection.
// Used by this standalone panel (escalation dispatch) and by the triage
// workspace's Partners section, whose advance lives in the gate strip.
// ---------------------------------------------------------------------------

export function readConflictAlertProps(detail: Record<string, unknown>): {
  missingIds: string[];
  backofficeMissing: BackofficeMissing | null;
} {
  return {
    missingIds: (detail.missing_item_ids as string[]) ?? [],
    backofficeMissing: readBackofficeMissing(detail),
  };
}

export function ReadinessConflictAlerts({
  missingIds,
  backofficeMissing,
}: Readonly<{
  missingIds: string[];
  backofficeMissing: BackofficeMissing | null;
}>) {
  const { assignments, setActiveAssignmentId } = useNpAssignmentsContext();
  return (
    <>
      {missingIds.length > 0 && (
        <Alert color="red" icon={<IconAlertCircle size={18} />} title="Cannot continue yet">
          <Stack gap={4}>
            <Text size="sm">
              {missingIds.length} item(s) are not yet covered by a ready assignment (need a locked
              NP, fee, and due date). Jump to:
            </Text>
            <Group gap="xs">
              {missingIds.map((id) => (
                <Anchor
                  key={id}
                  href={`#scope-tile-${id}`}
                  size="xs"
                  data-testid={`missing-item-link-${id}`}
                >
                  {id.slice(0, 8)}…
                </Anchor>
              ))}
            </Group>
          </Stack>
        </Alert>
      )}

      {backofficeMissing && (
        <BackofficeMissingBanner
          missing={backofficeMissing}
          assignments={assignments}
          onOpenAssignment={setActiveAssignmentId}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Backoffice rejection banner (missing partners / files / subjects / components)
// ---------------------------------------------------------------------------

interface BackofficeMissingBannerProps {
  missing: BackofficeMissing;
  assignments: BackofficeAssignment[];
  onOpenAssignment: (id: string) => void;
}

function BackofficeMissingBanner({
  missing,
  assignments,
  onOpenAssignment,
}: Readonly<BackofficeMissingBannerProps>) {
  // Map a flagged partner email → the assignment(s) it's locked on, so the
  // analyst can jump straight to the one that needs fixing.
  const assignmentsByEmail = new Map<string, { assignment: BackofficeAssignment; index: number }>();
  assignments.forEach((a, index) => {
    const email = a.networkPartner?.email?.toLowerCase();
    if (email) {
      assignmentsByEmail.set(email, { assignment: a, index });
    }
  });

  const filesOnly =
    missing.missingPartners.length === 0 &&
    missing.missingSubjects.length === 0 &&
    missing.missingComponents.length === 0 &&
    missing.missingDocuments.length > 0;

  return (
    <Alert
      color="red"
      icon={filesOnly ? <IconFileAlert size={18} /> : <IconUserExclamation size={18} />}
      title="Backoffice couldn't create the assignments"
      data-testid="backoffice-missing-banner"
    >
      <Stack gap="sm">
        <Text size="sm">
          Fix the flagged items below, then press “Continue to Ready” again. Nothing was sent to
          Backoffice.
        </Text>

        {missing.missingPartners.length > 0 && (
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              Network Partners not found in Backoffice
            </Text>
            <Text size="xs" c="dimmed">
              These partners aren’t provisioned in Backoffice. Provision them there, or pick a
              different NP for the assignment.
            </Text>
            <List size="sm" spacing={2}>
              {missing.missingPartners.map((email) => {
                const hit = assignmentsByEmail.get(email.toLowerCase());
                return (
                  <List.Item key={email} data-testid={`missing-partner-${email}`}>
                    <Group gap="xs">
                      <Text size="sm">{email}</Text>
                      {hit && (
                        <Anchor
                          size="xs"
                          component="button"
                          type="button"
                          onClick={() => onOpenAssignment(hit.assignment.id)}
                        >
                          open assignment A{hit.index + 1} →
                        </Anchor>
                      )}
                    </Group>
                  </List.Item>
                );
              })}
            </List>
          </Stack>
        )}

        {missing.missingDocuments.length > 0 && (
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              Files that couldn’t be transferred
            </Text>
            <Text size="xs" c="dimmed">
              Re-attach these to their assignment (the upload link may have expired), then retry.
            </Text>
            <List size="sm" spacing={2}>
              {missing.missingDocuments.map((doc) => (
                <List.Item key={doc} data-testid={`missing-document-${doc}`}>
                  {doc}
                </List.Item>
              ))}
            </List>
          </Stack>
        )}

        {missing.missingSubjects.length > 0 && (
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              Subjects not recognised by Backoffice
            </Text>
            <List size="sm" spacing={2}>
              {missing.missingSubjects.map((s) => (
                <List.Item key={s}>{s}</List.Item>
              ))}
            </List>
          </Stack>
        )}

        {missing.missingComponents.length > 0 && (
          <Stack gap={2}>
            <Text size="sm" fw={600}>
              Scope components not recognised by Backoffice
            </Text>
            <List size="sm" spacing={2}>
              {missing.missingComponents.map((c) => (
                <List.Item key={c}>{c}</List.Item>
              ))}
            </List>
          </Stack>
        )}
      </Stack>
    </Alert>
  );
}
